//* 코인 스토어 팝업 - 실제 코인 상품 목록 표시
// 2025.01.28
// 희문

import React, { useEffect, useState, useRef } from "react";
import * as WalletApi from "api/student/v2/wallet.api";
import type { ICoinProductItem } from "api/student/v2/wallet.api";
import {
    getSalesChannelForCoinProducts,
    getIsIos,
    isInApp,
    purchaseConsumable,
    purchaseVerifyResult,
    registerFlutterPurchaseEvent,
    unregisterFlutterPurchaseEvent,
    PURCHASE_EVENT_CODES,
    type FlutterPurchaseEventPayload,
} from "helpers/InApp.helper";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

interface CoinStorePopupProps {
    onClose: () => void;
    coinBalance?: number;
    /** 결제 완료 후 잔액 갱신용 (웹 즉시 결제 시 호출) */
    onPurchaseSuccess?: () => void;
}

function formatPrice(amount: number, currencyCode: string | null): string {
    const code = (currencyCode || "KRW").toUpperCase();
    const value = Number(amount);
    if (code === "KRW") {
        return `₩${value.toLocaleString("ko-KR")}`;
    }
    if (code === "USD") {
        return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `${value.toLocaleString()} ${code}`;
}

const isIapDevEnv = () =>
    process.env.REACT_APP_ENV === "local" || process.env.REACT_APP_ENV === "dev";
const alertStep = (title: string, detail?: string) => {
    if (isIapDevEnv()) alert(`[IAP] ${title}${detail ? `\n${detail}` : ""}`);
};

const CoinStorePopup: React.FC<CoinStorePopupProps> = ({ onClose, coinBalance = 0, onPurchaseSuccess }) => {
    const [products, setProducts] = useState<ICoinProductItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [purchasingProductId, setPurchasingProductId] = useState<number | null>(null);
    const [purchaseSuccessMessage, setPurchaseSuccessMessage] = useState<string | null>(null);
    const onPurchaseSuccessRef = useRef(onPurchaseSuccess);
    onPurchaseSuccessRef.current = onPurchaseSuccess;
    /** IAP 구매 시 검증 완료 후 completePurchase 호출에 쓸 coinProductId (이벤트 핸들러 클로저용) */
    const purchasingCoinProductIdRef = useRef<number | null>(null);

    useEffect(() => {
        let mounted = true;
        const fetchProducts = async () => {
            try {
                setLoading(true);
                setError(null);
                const salesChannelCode = await getSalesChannelForCoinProducts();
                const res = await WalletApi.getCoinProducts({ salesChannelCode });
                if (mounted && res?.list) {
                    setProducts(res.list);
                }
            } catch (e: any) {
                if (mounted) {
                    setError(e?.message || getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtLoadError"));
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };
        fetchProducts();
        return () => {
            mounted = false;
        };
    }, []);

    // App → Web: Flutter IAP 이벤트 수신 (verify_required → 서버 검증 → purchaseVerifyResult 호출)
    useEffect(() => {
        if (!isInApp()) return;
        const handler = async (event: FlutterPurchaseEventPayload) => {
            console.log("[CoinStorePopup] onFlutterPurchaseEvent", event);
            alertStep("이벤트 수신", `code=${event.code}\nok=${event.ok}\nmessage=${event.message ?? "-"}`);
            const msg = event.message ?? "";
            const refreshAndUnlock = () => {
                onPurchaseSuccessRef.current?.();
                setPurchasingProductId(null);
                purchasingCoinProductIdRef.current = null;
            };
            const failAndUnlock = (errorMessage: string) => {
                setError(errorMessage);
                setPurchasingProductId(null);
                purchasingCoinProductIdRef.current = null;
            };

            switch (event.code) {
                case PURCHASE_EVENT_CODES.verify_required: {
                    const extra = event.extra;
                    alertStep("verify_required 처리 시작", `verifyKey=${extra?.verifyKey ?? "-"}`);
                    if (!extra?.verifyKey || extra.purchase == null) {
                        alertStep("verify_required 실패", "검증 정보 없음");
                        failAndUnlock("검증 정보가 없습니다.");
                        return;
                    }
                    try {
                        const isIos = await getIsIos();
                        alertStep("서버 검증 요청", `platform=${isIos ? "ios" : "android"}`);
                        const coinProductId = purchasingCoinProductIdRef.current;
                        const res = await WalletApi.verifyIapPurchase({
                            purchase: extra.purchase,
                            verifyKey: extra.verifyKey,
                            requireSigned: !!extra.requireSigned,
                            platform: isIos ? "ios" : "android",
                            productId: event.productId,
                            coinProductId: coinProductId ?? undefined,
                            storeSkuId: event.productId ?? null,
                        });
                        alertStep("서버 검증 결과", `verified=${res.verified} hasToken=${!!res.token} balance=${res.balance ?? "-"}`);
                        if (res.verified) {
                            if (res.balance != null && coinProductId != null) {
                                alertStep("스타디아 적립 완료(verify 한 번에 처리)", `balance=${res.balance}`);
                            } else if (coinProductId != null) {
                                try {
                                    await WalletApi.completePurchase({
                                        coinProductId,
                                        storeSkuId: event.productId ?? null,
                                        storeTransactionId: res.storeTransactionId ?? null,
                                        platform: res.platform ?? (await getIsIos() ? "ios" : "android"),
                                    });
                                    alertStep("스타디아 적립 완료", `coinProductId=${coinProductId}`);
                                } catch (completeErr: any) {
                                    alertStep("스타디아 적립 오류", completeErr?.message ?? "completePurchase failed");
                                    purchaseVerifyResult({ verifyKey: extra.verifyKey, verified: true, reason: "server_verified" });
                                    failAndUnlock("결제 검증은 완료되었으나 스타디아 적립 중 오류가 발생했습니다. 고객센터에 문의해 주세요.");
                                    break;
                                }
                            }
                            if (extra.requireSigned && res.token) {
                                purchaseVerifyResult({ verifyKey: extra.verifyKey, token: res.token, reason: "token_verify" });
                            } else {
                                purchaseVerifyResult({ verifyKey: extra.verifyKey, verified: true, reason: "server_verified" });
                            }
                        } else {
                            purchaseVerifyResult({ verifyKey: extra.verifyKey, verified: false, reason: "server_rejected" });
                            failAndUnlock("결제 검증에 실패했습니다.");
                        }
                    } catch (e: any) {
                        alertStep("서버 검증 오류", e?.message ?? "unknown");
                        purchaseVerifyResult({ verifyKey: extra.verifyKey, verified: false, reason: "server_error" });
                        failAndUnlock(e?.message || "검증 요청 중 오류가 발생했습니다.");
                    }
                    break;
                }
                case PURCHASE_EVENT_CODES.verified_purchase:
                    alertStep("결제 완료", "verified_purchase – 스타디아 적립");
                    setPurchaseSuccessMessage(getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtPurchaseSuccess") ?? "스타디아가 적립되었습니다.");
                    setTimeout(() => setPurchaseSuccessMessage(null), 3000);
                    refreshAndUnlock();
                    break;
                case PURCHASE_EVENT_CODES.iap_not_available:
                    alertStep("실패", `iap_not_available\n${msg || "스토어 결제 불가"}`);
                    failAndUnlock(msg || "스토어 결제를 사용할 수 없습니다. 계정/기기를 확인해 주세요.");
                    break;
                case PURCHASE_EVENT_CODES.product_query_error:
                    alertStep("실패", `product_query_error\n${msg || "상품 조회 오류"}`);
                    failAndUnlock(msg || "상품 정보를 불러올 수 없습니다.");
                    break;
                case PURCHASE_EVENT_CODES.verification_rejected:
                    alertStep("실패", `verification_rejected\n${msg || "검증 거절"}`);
                    failAndUnlock(msg || "결제 검증이 거절되었습니다. 권한이 부여되지 않았습니다.");
                    break;
                case PURCHASE_EVENT_CODES.error:
                default:
                    if (!event.ok) {
                        alertStep("실패", `code=${event.code}\n${msg || "오류"}`);
                        failAndUnlock(msg || "결제 처리 중 오류가 발생했습니다.");
                    } else {
                        setPurchasingProductId(null);
                    }
                    break;
            }
        };
        registerFlutterPurchaseEvent(handler);
        return () => unregisterFlutterPurchaseEvent();
    }, []);

    const handleProductClick = async (item: ICoinProductItem) => {
        // if (purchasingProductId != null) return;
        const canPay = item.storeSkuId != null && item.storeSkuId !== "";

        // App 내 결제 처리 (취소 시 네이티브가 콜백하지 않으면 프로미스가 끝나지 않으므로 타임아웃으로 재클릭 가능하도록 함)
        if (isInApp()) {
            if (!canPay) return;
            alertStep("앱 결제 시작", `productId=${item.storeSkuId} requestId=${item.id}`);
            setPurchasingProductId(item.id);
            purchasingCoinProductIdRef.current = item.id;
            try {
                purchaseConsumable({ productId: item.storeSkuId!, requestId: String(item.id) });
            } catch (e: any) {
                if (e?.message === "PURCHASE_TIMEOUT") {
                    setError(null);
                } else {
                    console.error("purchaseConsumable failed:", e);
                    setError(e?.message || getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtLoadError"));
                }
            }
            return;
        }

        // Web 결제 처리: 완료 후 purchasingProductId를 반드시 초기화해 같은 상품 재클릭 가능
        setError(null);
        setPurchasingProductId(item.id);
        alertStep("웹 즉시결제 요청", `coinProductId=${item.id}`);
        try {
            const result = await WalletApi.completePurchase({
                coinProductId: item.id,
                storeSkuId: item.storeSkuId ?? null,
            });
            alertStep("웹 즉시결제 완료", `${result.totalCoinAmount} 스타디아 적립\nbalance=${result.balance}`);
            setPurchaseSuccessMessage(
                getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtPurchaseSuccess") || `${result.orderTitle} ${result.totalCoinAmount.toLocaleString("ko-KR")} 스타디아 적립 완료`
            );
            onPurchaseSuccess?.();
            setTimeout(() => setPurchaseSuccessMessage(null), 3000);
        } catch (e: any) {
            alertStep("웹 즉시결제 오류", e?.message ?? "unknown");
            console.error("completePurchase failed:", e);
            setError(e?.message || getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtLoadError"));
        } finally {
            setPurchasingProductId(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen h-screen top-0 left-0 p-4 !m-0">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">{getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtTitle")}</h3>
                    <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-4">
                    {loading ? (
                        <div className="py-8 text-center text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtLoading")}</div>
                    ) : products.length === 0 ? (
                        error ? (
                            <div className="py-8 text-center text-red-500">{error}</div>
                        ) : (
                            <div className="py-8 text-center text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtNoProducts")}</div>
                        )
                    ) : (
                        <>
                        {error && (
                            <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm py-2 px-3 text-center">
                                {error}
                            </div>
                        )}
                        <div className="overflow-x-auto overflow-y-hidden -mx-1 px-1">
                            <div className="flex gap-4 sm:gap-5 flex-nowrap pb-2">
                                {products.map((item) => {
                                    const bonus = item.bonusCoinAmount || 0;
                                    const totalCoin = item.grantCoinAmount + bonus;
                                    const hasBadge = !!(item.isRecommended || item.isHit);
                                    const isRecommended = !!item.isRecommended;
                                    const priceText =
                                        item.priceAmount != null && item.priceAmount > 0
                                            ? formatPrice(item.priceAmount, item.priceCurrencyCode)
                                            : null;
                                    const canPay = (item.storeSkuId ?? "") !== "";
                                    const isPurchasing = purchasingProductId === item.id;
                                    const isWeb = !isInApp();
                                    const canClick = isWeb ? !isPurchasing : (canPay && !isPurchasing);
                                    return (
                                        <div
                                            key={item.id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => canClick && handleProductClick(item)}
                                            onKeyDown={(e) => {
                                                if (!canClick) return;
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    handleProductClick(item);
                                                }
                                            }}
                                            className={`
                                                flex-shrink-0 w-[200px] sm:w-[220px]
                                                relative bg-white rounded-2xl shadow-sm border overflow-hidden
                                                flex flex-col min-h-[220px] sm:min-h-[240px]
                                                transition-shadow hover:shadow-md
                                                ${canClick ? "cursor-pointer" : "cursor-not-allowed opacity-90"}
                                                ${hasBadge ? (isRecommended ? "border-pink-200" : "border-purple-200") : "border-gray-200"}
                                            `}
                                        >
                                            {hasBadge && (
                                                <div className="absolute top-3 left-3 z-10">
                                                    <span
                                                        className={`
                                                            inline-block px-3 py-1.5 rounded-lg text-xs font-semibold
                                                            ${isRecommended ? "bg-pink-100 text-pink-700" : "bg-purple-100 text-purple-700"}
                                                        `}
                                                    >
                                                        {isRecommended
                                                            ? getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.badgeRecommended")
                                                            : getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.badgePopular")}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="pt-4 pb-4 sm:pb-5 flex justify-center">
                                                <img
                                                    src="/imgs/common/stardia.svg"
                                                    alt=""
                                                    className="w-16 h-16 sm:w-20 sm:h-20"
                                                    aria-hidden
                                                />
                                            </div>
                                            <div className="px-5 pt-1 pb-0 text-center">
                                                <p className={`text-sm ${bonus > 0 ? "font-bold text-black" : "text-gray-500"}`}>
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtBonus")}{" "}
                                                    <span className="tabular-nums">+{(bonus || 0).toLocaleString("ko-KR")}</span>
                                                </p>
                                            </div>
                                            <div className="flex-1 flex items-center justify-center px-5 pt-0 pb-4 sm:pb-6">
                                                <span className="text-3xl sm:text-4xl font-extrabold text-[#CF3688] tabular-nums">
                                                    {totalCoin.toLocaleString("ko-KR")}
                                                </span>
                                            </div>
                                            <div className="px-4 pb-4 pt-0">
                                                <div className="bg-[#ffd700] rounded-xl py-3 px-4 text-right">
                                                    {isPurchasing ? (
                                                        <span className="text-sm font-medium text-gray-600">
                                                            {getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtLoading")}
                                                        </span>
                                                    ) : priceText ? (
                                                        <span className="text-lg sm:text-xl font-bold text-black tabular-nums">
                                                            {priceText}
                                                        </span>
                                                    ) : (
                                                        <span className="text-sm font-medium text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtPriceInquiry")}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        </>
                    )}

                    {purchaseSuccessMessage && (
                        <div className="rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm py-2 px-3 text-center">
                            {purchaseSuccessMessage}
                        </div>
                    )}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="text-center">
                            <div className="text-sm text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "coinStore.txtCurrentBalance")}</div>
                            <div className="text-2xl font-bold text-yellow-500">
                                {(coinBalance || 0).toLocaleString("ko-KR")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CoinStorePopup;
